import React, { FC, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { ShoppingCartContainer } from '@components/organisms/ShoppingCartContainer';
import { Card } from '@components/atoms/Card';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Container } from '@components/atoms/Container';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { formatDateTime, formatMultibancoReference, formatPhoneNumber, formatPrice } from '@utils/format';
import styles from './Feedback.module.css';
import { SITE_URLS } from '@constants';
import { useData } from '@hooks/useData';
import { useCustomNavigate } from '@hooks/useCustomNavigate';

const CURRENT_STEP = 5;

export const Feedback: FC = () => {
    const { navigate } = useCustomNavigate();
    const { login, createdOrder } = useData();
    const { t } = useTranslation();

    const translations = {
        title: t('feedback-component.title'),
        orderNumberLabel: t('feedback-component.order-number.label'),
        mbEntityLabel: t('feedback-component.multibanco.entity'),
        mbReferenceLabel: t('feedback-component.multibanco.reference'),
        mbValueLabel: t('feedback-component.multibanco.value'),
        priceLabel: t('feedback-component.price.label'),
        feedbackMessage: t('feedback-component.feedback-message', {
            returnObjects: true,
            name: `${login?.data?.user?.name} ${login?.data?.user?.lastName}`,
        }) as string[],
        feedbackMessageWaitingMbwayPayment: t('feedback-component.feedback-message-waiting-mbway-payment', {
            phoneNumber: formatPhoneNumber(createdOrder?.data?.payment_phone_number || ''),
        }),
        feedbackMessageWaitingMultibancoPayment: t('feedback-component.feedback-message-waiting-multibanco-payment', {
            expiration: formatDateTime(createdOrder?.data?.payment_expiration || ''),
        }),
        feedbackMessageWaitingCardPayment: t('feedback-component.feedback-message-waiting-card-payment'),
        feedbackMessageSentToMail: t('feedback-component.feedback-message-sent-to-email'),
        continueShoppingButton: t('feedback-component.continue-shopping.button'),
    };

    const openedCardPaymentRef = useRef(false);

    useEffect(() => {
        translations.feedbackMessageWaitingMbwayPayment = t('feedback-component.feedback-message-waiting-mbway-payment', {
            phoneNumber: formatPhoneNumber(createdOrder?.data?.payment_phone_number || ''),
        });
        translations.feedbackMessageWaitingMultibancoPayment = t(
            'feedback-component.feedback-message-waiting-multibanco-payment',
            {
                expiration: formatDateTime(createdOrder?.data?.payment_expiration || ''),
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createdOrder?.data?.payment_phone_number, createdOrder?.data?.payment_expiration]);

    // if user is not logged in, he can't access this page
    useEffect(() => {
        if (login === null) {
            navigate(`/${SITE_URLS.login}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    // redirect for card payment
    useEffect(() => {
        if (createdOrder?.data?.payment_url && !openedCardPaymentRef.current) {
            openedCardPaymentRef.current = true;
            // Open the external link in a new tab automatically
            window.open(createdOrder?.data?.payment_url, '_blank', 'noopener,noreferrer');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createdOrder?.data?.payment_url]);

    return (
        <ShoppingCartContainer
            step={CURRENT_STEP}
            title={translations.title}
            action={
                <Anchor href={SITE_URLS.products} mode={AnchorMode.primary}>
                    {translations.continueShoppingButton}
                </Anchor>
            }>
            <Card classes={styles['order-feedback-card']}>
                <Container classes="text-center" customSize="custom-medium">
                    <>
                        <Typography color="success" fontWeight="900" fontSize="36" tagVariant={TypographyTagVariants.span}>
                            <i className="fa-solid fa-badge-check" />
                        </Typography>
                        <Typography classes="my-4 py-1" fontWeight="800" fontSize="16">
                            {`${translations.orderNumberLabel}: ${createdOrder?.data?.order_number}`}
                        </Typography>
                        <div className="my-4 py-1">
                            {[...translations.feedbackMessage].map((line, index) => (
                                <Typography
                                    key={`line-${index}`}
                                    color="success"
                                    fontWeight="800"
                                    fontSize="20"
                                    breakpointsStyles={{ md: { fontSize: '24' } }}>
                                    {line}
                                </Typography>
                            ))}
                        </div>
                        <hr className="my-4 py-1" />
                        {createdOrder?.data?.payment_method?.image?.src ? (
                            <div className="my-4 py-1">
                                <img
                                    src={createdOrder.data.payment_method.image.src}
                                    alt={createdOrder.data.payment_method.name}
                                />
                            </div>
                        ) : (
                            <></>
                        )}

                        {createdOrder?.data?.payment_method?.method === 'mbway' && (
                            <>
                                <div
                                    className={`${styles['payment-specific-feedback']} my-4`}
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(translations.feedbackMessageWaitingMbwayPayment),
                                    }}></div>
                                <Typography classes="my-4 py-1" fontWeight="800" fontSize="16">
                                    <>
                                        {`${translations.priceLabel}: `}
                                        <Typography tagVariant={TypographyTagVariants.span} color="primary">
                                            {formatPrice(createdOrder?.data?.total)}
                                        </Typography>
                                    </>
                                </Typography>
                            </>
                        )}

                        {createdOrder?.data?.payment_method?.method === 'multibanco' && (
                            <>
                                <div
                                    className={`${styles['payment-specific-feedback']} my-4`}
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(translations.feedbackMessageWaitingMultibancoPayment),
                                    }}></div>
                                <div className="my-4 text-start d-inline-block">
                                    <Typography classes="py-1" fontWeight="800" fontSize="16">
                                        <>
                                            {`${translations.mbEntityLabel}: `}
                                            <Typography tagVariant={TypographyTagVariants.span} color="primary">
                                                {createdOrder.data.payment_entity}
                                            </Typography>
                                        </>
                                    </Typography>
                                    <Typography classes="py-1" fontWeight="800" fontSize="16">
                                        <>
                                            {`${translations.mbReferenceLabel}: `}
                                            <Typography tagVariant={TypographyTagVariants.span} color="primary">
                                                {formatMultibancoReference(createdOrder.data.payment_reference || '')}
                                            </Typography>
                                        </>
                                    </Typography>
                                    <Typography classes="py-1" fontWeight="800" fontSize="16">
                                        <>
                                            {`${translations.mbValueLabel}: `}
                                            <Typography tagVariant={TypographyTagVariants.span} color="primary">
                                                {formatPrice(createdOrder?.data?.total)}
                                            </Typography>
                                        </>
                                    </Typography>
                                </div>
                            </>
                        )}

                        {createdOrder?.data?.payment_method?.method === 'card' && (
                            <>
                                <div
                                    className={`${styles['payment-specific-feedback']} my-4`}
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(translations.feedbackMessageWaitingCardPayment),
                                    }}></div>
                                <Typography classes="my-4 py-1" fontWeight="800" fontSize="16">
                                    <>
                                        {`${translations.priceLabel}: `}
                                        <Typography tagVariant={TypographyTagVariants.span} color="primary">
                                            {formatPrice(createdOrder?.data?.total)}
                                        </Typography>
                                    </>
                                </Typography>
                            </>
                        )}

                        <hr className="my-4 py-1" />
                        <Typography fontSize="16">{translations.feedbackMessageSentToMail}</Typography>
                    </>
                </Container>
            </Card>
        </ShoppingCartContainer>
    );
};
